import { useState, useCallback, useMemo } from 'react';
import styles from './Interactive.module.css';

interface Category {
  name: string;
  items: string[];
}

interface CategorizationConfig {
  categories: Category[];
}

interface CategorizationProps {
  config: CategorizationConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
}

export function Categorization({ config, onSubmit, disabled, showResult }: CategorizationProps) {
  // Get all items and shuffle them
  const allItems = useMemo(() => {
    const items = config.categories.flatMap((c) => c.items);
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [config.categories]);

  // Track which items are in which category (user's sorting)
  const [categorized, setCategorized] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    config.categories.forEach((c) => {
      initial[c.name] = [];
    });
    return initial;
  });

  // Items not yet categorized
  const uncategorized = useMemo(() => {
    const sorted = new Set(Object.values(categorized).flat());
    return allItems.filter((item) => !sorted.has(item));
  }, [allItems, categorized]);

  const [draggingItem, setDraggingItem] = useState<string | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);

  // Get correct category for an item
  const getCorrectCategory = useCallback((item: string): string | null => {
    for (const cat of config.categories) {
      if (cat.items.includes(item)) {
        return cat.name;
      }
    }
    return null;
  }, [config.categories]);

  const handleDragStart = useCallback((item: string) => {
    if (disabled) return;
    setDraggingItem(item);
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent, categoryName: string) => {
    e.preventDefault();
    if (disabled) return;
    setDragOverCategory(categoryName);
  }, [disabled]);

  const handleDragLeave = useCallback(() => {
    setDragOverCategory(null);
  }, []);

  const handleDrop = useCallback((categoryName: string) => {
    if (disabled || !draggingItem) return;

    // Remove item from previous category if it was there
    setCategorized((prev) => {
      const next = { ...prev };
      for (const cat of Object.keys(next)) {
        next[cat] = next[cat].filter((item) => item !== draggingItem);
      }
      // Add to new category
      next[categoryName] = [...next[categoryName], draggingItem];
      return next;
    });

    setDraggingItem(null);
    setDragOverCategory(null);
  }, [disabled, draggingItem]);

  const handleDragEnd = useCallback(() => {
    setDraggingItem(null);
    setDragOverCategory(null);
  }, []);

  const handleSubmit = () => {
    if (disabled) return;
    onSubmit(JSON.stringify(categorized));
  };

  const getItemClass = (item: string, inCategory?: string) => {
    let className = styles.categorizationItem;

    if (disabled) {
      className += ` ${styles.disabled}`;
    }

    if (draggingItem === item) {
      className += ` ${styles.dragging}`;
    }

    if (showResult && inCategory) {
      const correctCat = getCorrectCategory(item);
      if (correctCat === inCategory) {
        className += ` ${styles.correct}`;
      } else {
        className += ` ${styles.incorrect}`;
      }
    }

    return className;
  };

  const getBucketClass = (categoryName: string) => {
    let className = styles.categoryBucket;
    if (dragOverCategory === categoryName) {
      className += ` ${styles.dragOver}`;
    }
    return className;
  };

  const allSorted = uncategorized.length === 0;

  return (
    <div className={styles.categorizationContainer}>
      {/* Unsorted items */}
      {uncategorized.length > 0 && (
        <div className={styles.categorizationItems}>
          {uncategorized.map((item, idx) => (
            <div
              key={idx}
              className={getItemClass(item)}
              draggable={!disabled}
              onDragStart={() => handleDragStart(item)}
              onDragEnd={handleDragEnd}
            >
              {item}
            </div>
          ))}
        </div>
      )}

      {uncategorized.length === 0 && !showResult && (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          All items sorted! Review your answers and submit.
        </p>
      )}

      {/* Category buckets */}
      <div className={styles.categorizationCategories}>
        {config.categories.map((category, idx) => (
          <div
            key={idx}
            className={getBucketClass(category.name)}
            onDragOver={(e) => handleDragOver(e, category.name)}
            onDragLeave={handleDragLeave}
            onDrop={() => handleDrop(category.name)}
          >
            <div className={styles.categoryBucketName}>{category.name}</div>
            <div className={styles.categoryBucketItems}>
              {categorized[category.name].map((item, itemIdx) => (
                <div
                  key={itemIdx}
                  className={getItemClass(item, category.name)}
                  draggable={!disabled}
                  onDragStart={() => handleDragStart(item)}
                  onDragEnd={handleDragEnd}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!showResult && (
        <div className={styles.inputContainer}>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={disabled || !allSorted}
          >
            Submit Categories
          </button>
        </div>
      )}
    </div>
  );
}
